/*eslint max-lines-per-function: ["error", 405]*/
/*eslint complexity: ["error", 24]*/
import React from 'react';
import 'react-circular-progressbar/dist/styles.css';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import {
  CreateUpdateEstimateItemDtoPositionTypeEnum,
  GetDictionaryDto,
  GetEstimateItemDto,
  GetExcelAndPokRowDto,
  GetExcelAndPokRowDtoPositionTypeEnum,
  GetExcelColumnsDto,
  GetExcelConfigDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridRecord,
} from '../../../common/components/Grid/GridDataTypes';
import momencik from '../../../common/momencik';
import numberFormatter from '../../../common/numberFormatter';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { useNavigation } from '../../../common/navigation';
import { isFieldHidden } from '../Estimates/CommissionService';
import mathUtils from '../../../utils/mathUtils';
import { EstimateItemFieldsDefinition } from '../../../common/pokCore/validation/estimateItemFields.definition';
import { usePokCore } from '../../../common/hooks/usePokCore';

import { AmountForm } from './AmountForm';
import { getExcelRowTeamName } from './SpecialistTeamMapperForm';

interface ExcelCompareGridProps {
  data: GetExcelAndPokRowDto[];
  columns: GetExcelColumnsDto[];
  config: GetExcelConfigDto;
  selectedKeys?: string[];
  refresh?: boolean;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  updateData?: (obj: Partial<GetExcelAndPokRowDto>) => void;
}

const getPokTeamName = (d: GetExcelAndPokRowDto) => {
  const team = d.estimateItem?.specialistTeam ?? d.estimateItem?.deliveryTeam;
  return team?.name
    ? `${team.name} (${team.company?.shortname || ''})`
    : undefined;
};

export const ExcelCompareGrid: React.FC<ExcelCompareGridProps> = props => {
  const { t, tk } = useTranslation('excelImportLng');
  const nav = useNavigation();
  const pok = usePokCore();
  const lang = pok.getLanguage();

  const amount = (value?: string) => {
    if (!value) {
      return '';
    }
    return numberFormatter(value || '', true);
  };

  const diff = (
    value1: string | undefined,
    value2: string | undefined,
    update: boolean,
    key?: string,
    positionType?: GetExcelAndPokRowDtoPositionTypeEnum,
  ) => {
    if (!update) {
      return value1;
    }
    if (value1 === value2) {
      return value1;
    }
    if (
      key &&
      isFieldHidden(
        key,
        positionType as CreateUpdateEstimateItemDtoPositionTypeEnum,
        props.config.profile,
      )
    ) {
      return '';
    }

    return <span className="bg-custom-light-red">{value1}</span>;
  };

  const amountEditor = (
    value: string | undefined,
    originalValue: string | undefined,
    fieldName: string,
    rowId: string,
    decimalScale?: number,
  ) => {
    return (
      <AmountForm
        amount={value || ''}
        originalAmount={originalValue || ''}
        decimalScale={decimalScale}
        handleUpdate={value =>
          props.updateData &&
          props.updateData({
            [fieldName]: value,
            rowId: rowId,
          })
        }
      />
    );
  };

  const openProject = (event: React.MouseEvent, projectId?: string) => {
    event.stopPropagation();
    nav.projectDetails(projectId || '', true, 'estimates');
  };

  const renderAmountField = (
    d: GetExcelAndPokRowDto,
    fieldName: string,
    update: boolean,
    positionType: GetExcelAndPokRowDtoPositionTypeEnum,
    decimalScale?: number,
  ) => {
    if (
      isFieldHidden(
        fieldName,
        positionType as CreateUpdateEstimateItemDtoPositionTypeEnum,
        props.config.profile,
      )
    ) {
      return '';
    }
    const value1 = d[fieldName as keyof GetExcelAndPokRowDto] as string;
    const orgValue = d[
      ('excel' +
        fieldName.charAt(0).toUpperCase() +
        fieldName.slice(1)) as keyof GetExcelAndPokRowDto
    ] as string;
    let value2 = undefined;
    if (d.estimateItem) {
      value2 = d.estimateItem[fieldName as keyof GetEstimateItemDto] as string;
    }
    return (
      <div className="noWrap">
        {diff(amount(value1), amount(value2), update)}
        {amountEditor(value1, orgValue, fieldName, d.rowId, decimalScale)}
      </div>
    );
  };

  const getOrderByNumberColumns = (d: GetExcelAndPokRowDto, keys: string[]) => {
    const columns: Record<string, number> = {};
    for (const key of keys) {
      let value = d[key as keyof GetExcelAndPokRowDto] ?? '-9999999';
      let pokValue = d.estimateItem
        ? d.estimateItem[key as keyof GetEstimateItemDto]
        : '-9999999';
      if (
        isFieldHidden(
          key,
          d.positionType as CreateUpdateEstimateItemDtoPositionTypeEnum,
          props.config.profile,
        )
      ) {
        value = '-9999999';
        pokValue = '-9999999';
      }
      if (typeof value === 'string') {
        columns[key] = mathUtils.parse(value);
        if (pokValue && typeof pokValue === 'string') {
          columns[key + 'Pok'] = mathUtils.parse(pokValue);
        }
      }
    }
    return columns;
  };

  const generateAmountFields = (d: GetExcelAndPokRowDto, update: boolean) => {
    const columns: Record<string, React.ReactNode> = {};
    for (const key of EstimateItemFieldsDefinition.amountFieldsKeys) {
      columns[key.name] = isHidden(key.name, d)
        ? ''
        : renderAmountField(d, key.name, update, d.positionType);
    }
    return columns;
  };

  const generatePercentageFields = (
    d: GetExcelAndPokRowDto,
    update: boolean,
  ) => {
    const columns: Record<string, React.ReactNode> = {};
    for (const key of EstimateItemFieldsDefinition.percentageFieldsKeys) {
      const value1 = d.estimateItem
        ? (d.estimateItem[key.name as keyof GetEstimateItemDto] as string)
        : undefined;
      const value2 =
        (d[key.name as keyof GetExcelAndPokRowDto] as string) ?? undefined;
      const percentageValue1 = value1
        ? `${mathUtils.parse(value1).toString()}%`
        : '';
      const percentageValue2 = value2
        ? `${mathUtils.parse(value2).toString()}%`
        : '';
      columns[key.name] = isHidden(key.name, d)
        ? ''
        : diff(percentageValue2, percentageValue1, update);
      columns[key.name + 'Pok'] = isHidden(key.name, d)
        ? ''
        : diff(percentageValue1, percentageValue2, update);
    }
    return columns;
  };

  const generatePokAmountFields = (
    d: GetExcelAndPokRowDto,
    update: boolean,
  ) => {
    const columns: Record<string, React.ReactNode> = {};
    for (const key of EstimateItemFieldsDefinition.amountFieldsKeys) {
      const value1 = d.estimateItem
        ? (d.estimateItem[key.name as keyof GetEstimateItemDto] as string)
        : undefined;
      const value2 = d[key.name as keyof GetExcelAndPokRowDto] as string;
      columns[key.name + 'Pok'] = isHidden(key.name, d)
        ? ''
        : diff(amount(value1), amount(value2), update);
    }
    return columns;
  };

  const generatePokOtherFields = (d: GetExcelAndPokRowDto, update: boolean) => {
    const columns: Record<string, React.ReactNode> = {};
    const specialistTeam =
      getExcelRowTeamName(d) ?? d.excelSpecialistTeam ?? undefined;
    const specialistTeamPok = getPokTeamName(d);
    columns['specialistTeam'] = diff(specialistTeam, specialistTeamPok, update);
    columns['specialistTeamPok'] = diff(
      specialistTeamPok,
      specialistTeam,
      update,
    );
    return columns;
  };

  const generateStringFields = (d: GetExcelAndPokRowDto, update: boolean) => {
    const columns: Record<string, React.ReactNode> = {};
    for (const key of EstimateItemFieldsDefinition.stringFields) {
      const value1 =
        (d[key.name as keyof GetExcelAndPokRowDto] as string) ?? undefined;
      const value2 =
        d.estimateItem && d.estimateItem[key.name as keyof GetEstimateItemDto]
          ? (d.estimateItem[key.name as keyof GetEstimateItemDto] as string)
          : undefined;
      columns[key.name] = isHidden(key.name, d)
        ? ''
        : diff(value1, value2, update, key.name, d.positionType);
      columns[key.name + 'Pok'] = isHidden(key.name, d)
        ? ''
        : diff(value2, value1, update, key.name, d.positionType);
    }
    return columns;
  };

  const generateDictionaryFields = (
    d: GetExcelAndPokRowDto,
    update: boolean,
  ) => {
    const columns: Record<string, React.ReactNode> = {};
    const suffix = lang === 'PL' ? 'ValuePl' : 'ValueEn';
    for (const key of EstimateItemFieldsDefinition.dictionaryFields) {
      const value1 =
        (d[(key.name + suffix) as keyof GetExcelAndPokRowDto] as string) ??
        undefined;
      const value2 =
        d.estimateItem && d.estimateItem[key.name as keyof GetEstimateItemDto]
          ? (
              d.estimateItem[
                key.name as keyof GetEstimateItemDto
              ] as GetDictionaryDto
            ).value
          : undefined;
      columns[key.name] = isHidden(key.name, d)
        ? ''
        : diff(value1, value2, update, key.name, d.positionType);
      columns[key.name + 'Pok'] = diff(
        value2,
        value1,
        update,
        key.name,
        d.positionType,
      );
    }
    return columns;
  };

  const generateBoolFields = (d: GetExcelAndPokRowDto) => {
    const columns: Record<string, string> = {};
    for (const key of EstimateItemFieldsDefinition.boolFields) {
      const value = d[key.name as keyof GetExcelAndPokRowDto] as boolean;
      columns[key.name] =
        value === undefined
          ? ''
          : value === true
            ? t(tk.common.yes)
            : t(tk.common.no);
    }
    return columns;
  };

  const isHidden = (key: string, d: GetExcelAndPokRowDto) => {
    return isFieldHidden(
      key,
      d.positionType as CreateUpdateEstimateItemDtoPositionTypeEnum,
      props.config.profile,
    );
  };

  const columns: GridColumn[] = [
    { header: t(tk.common.status), property: 'status' },
  ];

  if (props.columns) {
    for (const column of props.columns) {
      if (column.columnName === 'projectNumber') {
        columns.push({
          header: t(tk.prLang.projectNumber),
          property: 'projectNumber',
          columnCssClass: 'noWrap',
        });
        continue;
      }
      if (
        column.columnName === 'month' ||
        column.columnName === 'startDate' ||
        column.columnName === 'endDate'
      ) {
        if (column.columnName !== 'endDate') {
          columns.push({
            header: t(tk.import.from),
            property: 'startDate',
            columnCssClass: 'noWrap',
          });
        }
        if (column.columnName !== 'startDate') {
          columns.push({
            header: t(tk.import.to),
            property: 'endDate',
            columnCssClass: 'noWrap',
          });
        }
        continue;
      }
      if (column.columnName === 'positionName') {
        columns.push({
          header: t(tk.config.positionInPok),
          property: 'positionName',
        });
        continue;
      }
      columns.push({
        header: lang === 'PL' ? column.columnNamePl : column.columnNameEn,
        property: column.columnName,
      });
      columns.push({
        header:
          (lang === 'PL' ? column.columnNamePl : column.columnNameEn) +
          ' (POK)',
        property: column.columnName + 'Pok',
      });
    }
    columns.push({ header: '', property: 'comments', noFilter: true });
  }

  const data: GridRecord[] = [];

  for (const d of props.data) {
    const update = d.estimateItem?.id ? true : false;
    const orderByNumberValues = getOrderByNumberColumns(
      d,
      EstimateItemFieldsDefinition.amountFieldsKeys.map(key => key.name),
    );
    const amountFields = generateAmountFields(d, update);
    const percentageFields = generatePercentageFields(d, update);
    const stringFields = generateStringFields(d, update);
    const dictionaryFields = generateDictionaryFields(d, update);
    const pokAmountFields = generatePokAmountFields(d, update);
    const boolFields = generateBoolFields(d);
    const otherFields = generatePokOtherFields(d, update);
    data.push({
      key: d.rowId,
      rowCssClass: d.estimateItem?.id ? 'success-row' : '',
      values: {
        ...amountFields,
        ...percentageFields,
        ...stringFields,
        ...dictionaryFields,
        ...pokAmountFields,
        ...boolFields,
        ...otherFields,
        rowId: d.rowId,
        status: d.estimateItem?.id ? t(tk.import.update) : t(tk.import.new),
        positionName: d.positionName,
        projectNumber: d.projectId && (
          <Button
            variant="link"
            className="p-0 m-0"
            onClick={e => openProject(e, d.projectId)}
          >
            {d.projectNumber}
          </Button>
        ),
        startDate: d.startDate ? momencik(d.startDate, 'YYYY-MM-DD') : '',
        endDate: d.endDate ? momencik(d.endDate, 'YYYY-MM-DD') : '',
        invoiceInstructionAttachment: d.invoiceInstructionAttachment,
        comments: d.comment && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{d.comment}</Tooltip>}
          >
            <Icon.ExclamationCircle className="icon-red" size={18} />
          </OverlayTrigger>
        ),
      },
      orderByNumberValues: {
        ...orderByNumberValues,
      },
      orderByStringValues: {
        projectNumber: d.projectNumber ?? '',
      },
    });
  }

  return (
    <Grid
      refresh={props.refresh}
      data={data}
      columns={columns}
      showFilter={true}
      availablePageSizes={[10, 20, 50, 100]}
      pageSize={100}
      enableMultiSelect={true}
      onRowClick={props.onRowClick}
      selectedKeys={props.selectedKeys}
      emptyGridMessage={t(tk.import.noPositions)}
      trClassName="amounts-align-right font-90"
      withFilterInColumns={true}
    />
  );
};
